import { useCallback, useEffect, useMemo, useState } from "react";
import { DeleteOutlined, EditOutlined, PlusOutlined, SearchOutlined } from "@ant-design/icons";
import { Button, Card, Form, Input, message, Modal, Popconfirm, Space, Switch, Table, Tag, Tooltip, Typography } from "antd";
import moment from "moment";
import { api, AUTH_USERNAME_KEY, isAdminRole } from "../services/apiClient";
import PromptUserFilter from "../components/prompt/PromptUserFilter";

const { Title, Text, Paragraph } = Typography;
const PARAM_PATTERN = /\{([^{}\s]+)}/g;
const REQUIRED_PARAMS = ["input", "output"];

function extractParameters(commandCode = "") {
  const values = [];
  const seen = new Set();
  let match;
  while ((match = PARAM_PATTERN.exec(commandCode)) !== null) {
    const value = match[1];
    if (!seen.has(value)) {
      seen.add(value);
      values.push(value);
    }
  }
  return values;
}

function formatDateTime(value) {
  if (!value) return "-";
  const date = moment(value);
  return date.isValid() ? date.format("DD/MM/YYYY HH:mm") : "-";
}

function validateRequiredFfmpegParameters(_, value = "") {
  const parameters = extractParameters(value);
  const missing = REQUIRED_PARAMS.filter((item) => !parameters.includes(item));
  return missing.length
    ? Promise.reject(new Error("Mã ffmpeg bắt buộc có đủ {input} và {output}"))
    : Promise.resolve();
}

export default function VideoFfmpegCommands() {
  const [items, setItems] = useState([]);
  const [search, setSearch] = useState("");
  const [editing, setEditing] = useState(null);
  const [open, setOpen] = useState(false);
  const [commandPreview, setCommandPreview] = useState("");
  const [selectedUserId, setSelectedUserId] = useState(null);
  const [form] = Form.useForm();
  const currentUsername = localStorage.getItem(AUTH_USERNAME_KEY);
  const isAdmin = isAdminRole();

  const isOwner = (item) => !item?.id || item.createdByUsername === currentUsername;

  const load = useCallback(async () => {
    try {
      const query = selectedUserId ? `?userId=${encodeURIComponent(selectedUserId)}` : "";
      setItems((await api.get(`/api/video-tools/ffmpeg-commands${query}`)) || []);
    } catch (error) {
      message.error(error.message);
    }
  }, [selectedUserId]);

  useEffect(() => { load(); }, [load]);

  const data = useMemo(() => {
    const keyword = search.toLowerCase();
    return items.filter((item) => `${item.commandName || ""} ${(item.parameters || []).join(" ")} ${item.description || ""} ${item.createdByUsername || ""} ${item.createdByEmail || ""}`.toLowerCase().includes(keyword));
  }, [items, search]);

  const openModal = (item = null) => {
    if (item && !isOwner(item)) {
      message.warning("Bạn chỉ được sửa lệnh do mình tạo");
      return;
    }
    setEditing(item);
    form.setFieldsValue(item || { status: "ACTIVE", isPublic: false });
    setCommandPreview(item?.commandCode || "");
    setOpen(true);
  };

  const closeModal = () => {
    setOpen(false);
    setEditing(null);
    setCommandPreview("");
    form.resetFields();
  };

  const save = async () => {
    try {
      const values = await form.validateFields();
      await api.post("/api/video-tools/ffmpeg-commands", { ...editing, ...values });
      message.success("Đã lưu lệnh FFmpeg");
      closeModal();
      load();
    } catch (error) {
      if (error.message) message.error(error.message);
    }
  };

  const remove = async (item) => {
    if (!isOwner(item)) {
      message.warning("Bạn chỉ được xóa lệnh do mình tạo");
      return;
    }
    try {
      await api.delete(`/api/video-tools/ffmpeg-commands/${item.id}`);
      message.success("Đã xóa lệnh FFmpeg");
      load();
    } catch (error) {
      message.error(error.message);
    }
  };

  const columns = [
    ...(isAdmin ? [{ title: "USER", width: 180, render: (_, item) => <Text>{item.createdByUsername || item.createdByEmail || "-"}</Text> }] : []),
    { title: "STT", width: 70, render: (_, __, index) => index + 1 },
    { title: "TÊN LỆNH", dataIndex: "commandName", render: (value) => <Text strong>{value}</Text> },
    {
      title: "USER TẠO",
      width: 180,
      render: (_, item) => (
        <div>
          <Text>{item.createdByUsername || "-"}</Text>
          {item.createdByEmail ? <><br /><Text type="secondary">{item.createdByEmail}</Text></> : null}
        </div>
      ),
    },
    {
      title: "PUBLIC",
      width: 100,
      render: (_, item) => <Tag color={item.isPublic ? "green" : "default"}>{item.isPublic ? "PUBLIC" : "PRIVATE"}</Tag>,
    },
    {
      title: "THAM SỐ",
      dataIndex: "parameters",
      render: (parameters = []) => (
        <Space wrap size={[4, 4]}>
          {parameters.length ? parameters.map((item) => <Tag key={item}>{item}</Tag>) : <Text type="secondary">-</Text>}
        </Space>
      ),
    },
    { title: "MÔ TẢ", dataIndex: "description", render: (value) => value || "-" },
    { title: "NGÀY TẠO", dataIndex: "createdAt", render: formatDateTime },
    { title: "NGÀY CẬP NHẬT", dataIndex: "updatedAt", render: formatDateTime },
    {
      title: "HÀNH ĐỘNG",
      width: 130,
      render: (_, item) => (
        <Space className="provider-actions">
          <Tooltip title={isOwner(item) ? "Sửa lệnh" : "Chỉ user tạo mới được sửa"}>
            <Button disabled={!isOwner(item)} aria-label="Sửa lệnh" className="ffmpeg-action-icon" icon={<EditOutlined />} onClick={() => openModal(item)} type="text" />
          </Tooltip>
          <Popconfirm title="Xóa lệnh này?" okText="Xóa" cancelText="Hủy" onConfirm={() => remove(item)}>
            <Tooltip title={isOwner(item) ? "Xóa lệnh" : "Chỉ user tạo mới được xóa"}>
              <Button disabled={!isOwner(item)} aria-label="Xóa lệnh" danger className="ffmpeg-action-icon" icon={<DeleteOutlined />} type="text" />
            </Tooltip>
          </Popconfirm>
        </Space>
      ),
    },
  ];

  const previewParameters = extractParameters(commandPreview);

  return (
    <>
      <div className="page-heading">
        <div>
          <Title level={3}>Render FFmpeg</Title>
          <Text type="secondary">Quản lý các mẫu lệnh FFmpeg dùng cho xử lý video.</Text>
        </div>
      </div>
      <Card className="criclebox">
        <div className="auto-toolbar">
          <Input className="auto-toolbar-search" allowClear prefix={<SearchOutlined />} placeholder="Tìm lệnh FFmpeg" value={search} onChange={(event) => setSearch(event.target.value)} />
          {isAdmin && <PromptUserFilter value={selectedUserId} onChange={setSelectedUserId} />}
          <Button className="auto-toolbar-primary" type="primary" icon={<PlusOutlined />} onClick={() => openModal()}>Thêm lệnh</Button>
        </div>
        <Table rowKey="id" columns={columns} dataSource={data} />
      </Card>
      <Modal width={760} title={editing ? "Sửa lệnh FFmpeg" : "Thêm lệnh FFmpeg"} visible={open} onCancel={closeModal} onOk={save} okText="Lưu" cancelText="Đóng">
        <Form form={form} layout="vertical">
          <Form.Item name="commandName" label="Tên lệnh" rules={[{ required: true, message: "Nhập tên lệnh" }]}>
            <Input />
          </Form.Item>
          <Form.Item
            name="commandCode"
            label="Mã ffmpeg"
            rules={[
              { required: true, message: "Nhập mã ffmpeg" },
              { validator: validateRequiredFfmpegParameters },
            ]}
          >
            <Input.TextArea rows={5} onChange={(event) => setCommandPreview(event.target.value)} placeholder='ffmpeg -i "{input}" -vf scale=1080:-2 "{output}"' />
          </Form.Item>
          <Form.Item label="Tham số">
            <Space wrap size={[4, 4]}>
              {previewParameters.length ? previewParameters.map((item) => <Tag key={item}>{item}</Tag>) : <Text type="secondary">Chưa phát hiện tham số</Text>}
            </Space>
          </Form.Item>
          <Form.Item name="description" label="Mô tả">
            <Input.TextArea rows={3} />
          </Form.Item>
          <Form.Item name="isPublic" label="Public" valuePropName="checked">
            <Switch checkedChildren="PUBLIC" unCheckedChildren="PRIVATE" />
          </Form.Item>
          {editing?.commandCode && (
            <Paragraph className="processed-video-path" type="secondary">
              Mã ffmpeg chỉ hiển thị trong màn thêm/sửa, không hiển thị ở danh sách.
            </Paragraph>
          )}
        </Form>
      </Modal>
    </>
  );
}
